import { useLayoutEffect, useRef } from "react";
import { Button } from "../../../design-v2";
import { turnStatusCopy } from "../domain/coachMessageOutcome";
import { formatDayLabel, formatTime } from "../domain/coachFormat";
import { TURN_PHASES } from "../domain/coachThread";
import { describeCoachLink, recallSources, RECALL_SOURCE_LABELS, summarizeCheckin } from "../domain/coachVocabulary";
import { ArrowRightIcon, RetryIcon } from "./coachIcons";

/*
 * The conversation, oldest first. Athlete messages are bubbles on the right;
 * coach replies are plain text on the left, followed by what belongs to that
 * reply: its links, then its sources (folded). Phase 3 proposal cards would
 * take the same place, under the reply they answer. No memory chip or card is
 * ever shown here (D2), and nothing administrative: memory and data live in
 * Coach settings.
 */

function DaySeparator({ at }) {
  return (
    <li className="lz-coach-day" role="separator">
      <span>{formatDayLabel(at)}</span>
    </li>
  );
}

function TypingIndicator() {
  return (
    <li className="lz-coach-row lz-coach-row--coach">
      <div className="lz-coach-typing" role="status" aria-label="Coach is replying">
        <span />
        <span />
        <span />
      </div>
    </li>
  );
}

function UserMessage({ item, onRetry, retryDisabled }) {
  const statusCopy = turnStatusCopy(item);
  const canRetry =
    item.retryable &&
    !item.redacted &&
    (item.status === TURN_PHASES.FAILED || item.status === TURN_PHASES.UNSENT);
  return (
    <li className="lz-coach-row lz-coach-row--user" data-status={item.status}>
      <div className={`lz-coach-bubble${item.redacted ? " lz-coach-bubble--redacted" : ""}`} title={formatTime(item.at)}>
        {item.redacted ? "Message erased" : item.text}
      </div>
      {statusCopy ? (
        <div className="lz-coach-delivery" role="alert">
          <span>{statusCopy}</span>
          {canRetry ? (
            <button
              type="button"
              className="lz-coach-delivery__retry"
              onClick={() => onRetry(item.clientMessageId)}
              disabled={retryDisabled}
            >
              <RetryIcon />
              Retry
            </button>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function LinkButtons({ links, onOpenLink }) {
  const described = (links || []).map(describeCoachLink).filter(Boolean);
  if (!described.length) return null;
  return (
    <div className="lz-coach-links">
      {described.map((link) => (
        <button
          key={link.key}
          type="button"
          className="lz-coach-link"
          aria-label={link.hint ? `${link.label}, ${link.hint}` : link.label}
          onClick={() => onOpenLink(link.target)}
        >
          <span className="lz-coach-link__label">{link.text}</span>
          {link.hint ? <span className="lz-coach-link__hint">{link.hint}</span> : null}
          <ArrowRightIcon />
        </button>
      ))}
    </div>
  );
}

// Folded by default: the reply is the answer, the sources only back it up.
function RecallSources({ recall, replyText }) {
  const excerpts = recallSources(recall, replyText);
  if (!excerpts.length) return null;
  return (
    <details className="lz-coach-recall">
      <summary>{excerpts.length === 1 ? "1 source" : `${excerpts.length} sources`}</summary>
      <ul>
        {excerpts.map((excerpt, index) => (
          <li key={`${excerpt.at}-${index}`}>
            <span className="lz-coach-recall__meta">
              {RECALL_SOURCE_LABELS[excerpt.source] || "Note"} · {formatDayLabel(excerpt.at)}
            </span>
            <span className="lz-coach-recall__text">{excerpt.text}</span>
          </li>
        ))}
      </ul>
    </details>
  );
}

function CoachReply({ event, onOpenLink }) {
  return (
    <li className="lz-coach-row lz-coach-row--coach">
      <div className="lz-coach-reply">
        {event.redacted ? <p className="lz-coach-reply__redacted">Message erased</p> : <p>{event.content}</p>}
        {!event.redacted ? <LinkButtons links={event.links} onOpenLink={onOpenLink} /> : null}
        {!event.redacted ? <RecallSources recall={event.recall} replyText={event.content} /> : null}
      </div>
    </li>
  );
}

// One quiet line in the thread, like a day separator: not a card.
function CheckinEntry({ event, onOpenLink }) {
  const checkin = event.checkin;
  const parts = summarizeCheckin(checkin);
  return (
    <li className="lz-coach-row lz-coach-row--event">
      <p className="lz-coach-checkin-entry">
        <span className="lz-coach-checkin-entry__title">Check-in</span>
        {" · "}
        {event.redacted ? "Erased" : parts.length ? parts.join(" · ") : "Sent without details"}
      </p>
      {checkin?.pain?.present ? <LinkButtons links={[{ kind: "PAIN_ISSUES" }]} onOpenLink={onOpenLink} /> : null}
    </li>
  );
}

export default function CoachThread({
  items,
  waiting,
  hasOlder,
  loadingOlder,
  onLoadOlder,
  onRetry,
  retryDisabled,
  onOpenLink,
}) {
  const listRef = useRef(null);
  const firstKeyRef = useRef(null);
  const lastKeyRef = useRef(null);
  const heightRef = useRef(0);

  // New message at the end: follow it. Older page at the top: keep the
  // athlete where they were.
  const firstKey = items[0]?.key || null;
  const lastKey = `${items[items.length - 1]?.key || ""}:${items[items.length - 1]?.status || ""}:${waiting}`;
  useLayoutEffect(() => {
    const scroller = document.scrollingElement || document.documentElement;
    const height = scroller.scrollHeight;
    let timer = null;
    if (lastKeyRef.current !== lastKey) {
      scroller.scrollTop = height;
      // A sheet closing in the same update restores its saved scroll position
      // right after this: follow the end once more on the next tick.
      timer = setTimeout(() => {
        scroller.scrollTop = scroller.scrollHeight;
      }, 0);
    } else if (firstKeyRef.current && firstKeyRef.current !== firstKey) {
      scroller.scrollTop += height - heightRef.current;
    }
    firstKeyRef.current = firstKey;
    lastKeyRef.current = lastKey;
    heightRef.current = height;
    return () => clearTimeout(timer);
  }, [firstKey, lastKey]);

  return (
    <div className="lz-coach-thread">
      {hasOlder ? (
        <div className="lz-coach-thread__older">
          <Button variant="ghost" size="sm" isLoading={loadingOlder} loadingLabel="Loading" onClick={onLoadOlder}>
            Show earlier messages
          </Button>
        </div>
      ) : null}
      <ol ref={listRef} className="lz-coach-thread__list" role="log" aria-live="polite" aria-label="Conversation with your coach">
        {items.map((item) => {
          switch (item.kind) {
            case "day":
              return <DaySeparator key={item.key} at={item.at} />;
            case "user":
              return <UserMessage key={item.key} item={item} onRetry={onRetry} retryDisabled={retryDisabled} />;
            case "coach":
              return <CoachReply key={item.key} event={item.event} onOpenLink={onOpenLink} />;
            case "checkin":
              return <CheckinEntry key={item.key} event={item.event} onOpenLink={onOpenLink} />;
            default:
              return null;
          }
        })}
        {waiting ? <TypingIndicator /> : null}
      </ol>
    </div>
  );
}
